<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
    overlay-opacity="0.9"
    content-class="modern-scores-dialog"
    z-index="3000"
    transition="scale-transition"
  >
    <v-card class="glass-card" elevation="0">
      <!-- Animated Header with Particles Background -->
      <div class="dialog-header">
        <div class="particles-bg"></div>
        <div class="header-content">
          <div class="header-left">
            <div class="trophy-container">
              <v-icon large color="white" class="trophy-icon">mdi-trophy-award</v-icon>
              <div class="trophy-glow"></div>
            </div>
            <div class="header-text">
              <h2 class="title-text">Challenges Leaderboard</h2>
              <!-- <p class="subtitle-text">Celebrating our top performers</p> -->
              <p v-if="leaderboardTitle" class="leaderboard-title">{{ leaderboardTitle }}</p>
            </div>
          </div>
          <v-btn
            icon
            @click="onClose"
            class="close-btn"
            color="white"
            x-large
          >
            <v-icon size="28">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Modern Tabs with Gradient -->
      <div class="custom-tabs">
        <div class="tabs-container">
          <button
            class="tab-btn"
            :class="{ active: tab === 0 }"
            @click="tab = 0"
          >
            <v-icon class="tab-icon">mdi-office-building</v-icon>
            <span>Top Agencies</span>
            <div class="tab-indicator"></div>
          </button>
          <button
            class="tab-btn"
            :class="{ active: tab === 1 }"
            @click="tab = 1"
          >
            <v-icon class="tab-icon">mdi-account-group</v-icon>
            <span>Top Volunteers</span>
            <div class="tab-indicator"></div>
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Agencies Tab -->
        <div v-show="tab === 0" class="tab-panel">
          <div class="content-wrapper">
            <div v-if="topScoresofAgency.length === 0" class="empty-state">
              <div class="empty-icon">
                <v-icon x-large>mdi-trophy-outline</v-icon>
              </div>
              <h3>No Agency Scores Yet!</h3>
              <p>Be the first to climb the leaderboard</p>
            </div>

            <div v-else class="leaderboard-list">
              <div
                v-for="(agency, index) in topScoresofAgency"
                :key="agency._id"
                class="leaderboard-item"
                :class="[`rank-${index + 1}`, { 'top-performer': index < 3 }]"
                :style="{ '--delay': `${index * 0.1}s` }"
              >
                <!-- Rank Number -->
                <div class="rank-number">
                  <span v-if="index < 3" class="medal">
                    <v-icon v-if="index === 0" color="#FFD700">mdi-medal</v-icon>
                    <v-icon v-else-if="index === 1" color="#C0C0C0">mdi-medal</v-icon>
                    <v-icon v-else color="#CD7F32">mdi-medal</v-icon>
                  </span>
                  <span v-else class="rank-text">#{{ index + 1 }}</span>
                </div>

                <!-- Avatar with Glow Effect -->
                <div class="avatar-container">
                  <div class="avatar-glow" :class="`glow-${index + 1}`"></div>
                  <v-avatar size="64" class="avatar-img">
                    <v-img :src="agency.agency.midThumbnail" :alt="agency.agency.name"></v-img>
                  </v-avatar>
                </div>

                <!-- Info Section -->
                <div class="info-section">
                  <h3 class="name">{{ agency.agency.name }}</h3>
                  <div class="points-container">
                    <div class="points-icon">
                      <v-icon color="#FF6B6B">mdi-star</v-icon>
                    </div>
                    <span class="points">{{ formatPoints(agency.total_points) }}</span>
                    <span class="points-label">points</span>
                  </div>
                </div>

                <!-- Progress Bar -->
                <div class="progress-section">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill"
                      :style="{ 
                        width: `${getProgressWidth(agency.total_points, topScoresofAgency[0]?.total_points)}%`,
                        background: getRankGradient(index)
                      }"
                    ></div>
                  </div>
                </div>

                <!-- Floating Particles -->
                <div class="floating-particles">
                  <div class="particle" v-for="i in 3" :key="i"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Volunteers Tab -->
        <div v-show="tab === 1" class="tab-panel">
          <div class="content-wrapper">
            <div v-if="topScoresofVolunteers.length === 0" class="empty-state">
              <div class="empty-icon">
                <v-icon x-large>mdi-account-group-outline</v-icon>
              </div>
              <h3>No Volunteer Scores Yet!</h3>
              <p>Start volunteering to see your name here</p>
            </div>

            <div v-else class="leaderboard-list">
              <div
                v-for="(volunteer, index) in topScoresofVolunteers"
                :key="volunteer._id"
                class="leaderboard-item"
                :class="[`rank-${index + 1}`, { 'top-performer': index < 3 }]"
                :style="{ '--delay': `${index * 0.1}s` }"
              >
                <!-- Rank Number -->
                <div class="rank-number">
                  <span v-if="index < 3" class="medal">
                    <v-icon v-if="index === 0" color="#FFD700">mdi-medal</v-icon>
                    <v-icon v-else-if="index === 1" color="#C0C0C0">mdi-medal</v-icon>
                    <v-icon v-else color="#CD7F32">mdi-medal</v-icon>
                  </span>
                  <span v-else class="rank-text">#{{ index + 1 }}</span>
                </div>

                <!-- Avatar with Glow Effect -->
                <div class="avatar-container">
                  <div class="avatar-glow" :class="`glow-${index + 1}`"></div>
                  <v-avatar size="64" class="avatar-img">
                    <v-img :src="volunteer.user.midThumbnail" :alt="`${volunteer.user.first_name} ${volunteer.user.last_name}`"></v-img>
                  </v-avatar>
                </div>

                <!-- Info Section -->
                <div class="info-section">
                  <h3 class="name">{{ volunteer.user.first_name }} {{ volunteer.user.last_name }}</h3>
                  <div class="points-container">
                    <div class="points-icon">
                      <v-icon color="#FF6B6B">mdi-star</v-icon>
                    </div>
                    <span class="points">{{ formatPoints(volunteer.total_points) }}</span>
                    <span class="points-label">points</span>
                  </div>
                  <div class="email-chip">
                    <v-icon small class="mr-1">mdi-email-outline</v-icon>
                    {{ volunteer.user.email }}
                  </div>
                </div>

                <!-- Progress Bar -->
                <div class="progress-section">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill"
                      :style="{ 
                        width: `${getProgressWidth(volunteer.total_points, topScoresofVolunteers[0]?.total_points)}%`,
                        background: getRankGradient(index)
                      }"
                    ></div>
                  </div>
                </div>

                <!-- Floating Particles -->
                <div class="floating-particles">
                  <div class="particle" v-for="i in 3" :key="i"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Footer -->
      <div class="dialog-footer">
        <v-btn
          @click="onClose"
          class="close-footer-btn"
          color="primary"
          outlined
          large
          rounded
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Close Leaderboard
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    },
    topScoresData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      tab: 0
    };
  },
  computed: {
    topScoresofAgency() {
      return this.topScoresData.topScoresofAgency || [];
    },
    topScoresofVolunteers() {
      return this.topScoresData.topScoresofVolunteers || [];
    },
      leaderboardTitle() {
    return this.topScoresData.title || '';
  }
  },
  methods: {
    formatPoints(points) {
      if (points >= 1000000) {
        return (points / 1000000).toFixed(1) + 'M';
      } else if (points >= 1000) {
        return (points / 1000).toFixed(1) + 'K';
      }
      return points.toLocaleString();
    },
    getProgressWidth(points, maxPoints) {
      if (!maxPoints) return 0;
      return Math.max((points / maxPoints) * 100, 5);
    },
    getRankGradient(index) {
      const gradients = [
        'linear-gradient(45deg, #FFD700, #FFA500)', // Gold
        'linear-gradient(45deg, #C0C0C0, #A0A0A0)', // Silver
        'linear-gradient(45deg, #CD7F32, #B8860B)', // Bronze
        'linear-gradient(45deg, #3b7ca1, #084468)', // Purple
        'linear-gradient(45deg, #f093fb, #f5576c)'  // Pink
      ];
      return gradients[Math.min(index, gradients.length - 1)];
    }
  },
};
</script>

<style scoped>
/* Main Dialog Styles */
.modern-scores-dialog {
  border-radius: 24px !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15) !important;
}

/* Header Styles */
.dialog-header {
  position: relative;
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  padding: 2rem;
  overflow: hidden;
}

.particles-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/><circle cx="40" cy="80" r="1" fill="rgba(255,255,255,0.1)"/></svg>');
  animation: float 20s infinite linear;
}

@keyframes float {
  0% { transform: translateY(0px); }
  100% { transform: translateY(-100px); }
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.trophy-container {
  position: relative;
}

.trophy-icon {
  position: relative;
  z-index: 2;
  animation: bounce 2s infinite;
}

.trophy-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  animation: pulse-glow 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@keyframes pulse-glow {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.3; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.6; }
}

.header-text {
  color: white;
}

.title-text {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(45deg, #fff, #f0f0f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle-text {
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: rotate(90deg) scale(1.1);
}

/* Custom Tabs */
.custom-tabs {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.tabs-container {
  display: flex;
  max-width: 600px;
  margin: 0 auto;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem 2rem;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  color: #666;
  font-weight: 500;
  font-size: 1rem;
}

.tab-btn.active {
  color: #084468;
}

.tab-btn:hover {
  background: rgba(102, 126, 234, 0.05);
}

.tab-icon {
  transition: all 0.3s ease;
}

.tab-btn.active .tab-icon {
  transform: scale(1.1);
}

.tab-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.tab-btn.active .tab-indicator {
  width: 80%;
}

/* Tab Content */
.tab-content {
  min-height: 60vh;
  max-height: 70vh;
  overflow-y: auto;
}

.tab-panel {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-wrapper {
  padding: 2rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
}

.empty-icon {
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.empty-state p {
  font-size: 1rem;
  opacity: 0.7;
}

/* Leaderboard Items */
.leaderboard-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.leaderboard-item {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideInUp 0.6s ease-out var(--delay);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.leaderboard-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
}

.leaderboard-item.top-performer {
  border: 2px solid;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.leaderboard-item.rank-1 {
  border-color: #FFD700;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%);
}

.leaderboard-item.rank-2 {
  border-color: #C0C0C0;
  background: linear-gradient(135deg, rgba(192, 192, 192, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%);
}

.leaderboard-item.rank-3 {
  border-color: #CD7F32;
  background: linear-gradient(135deg, rgba(205, 127, 50, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%);
}

/* Rank Number */
.rank-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 50px;
  font-size: 1.5rem;
  font-weight: 700;
}

.medal {
  font-size: 2rem;
  animation: spin 3s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.rank-text {
  color: #667eea;
  font-size: 1.2rem;
}

/* Avatar */
.avatar-container {
  position: relative;
}

.avatar-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  opacity: 0.3;
  animation: pulse 2s infinite;
}

.avatar-glow.glow-1 {
  background: radial-gradient(circle, #FFD700, transparent);
}

.avatar-glow.glow-2 {
  background: radial-gradient(circle, #C0C0C0, transparent);
}

.avatar-glow.glow-3 {
  background: radial-gradient(circle, #CD7F32, transparent);
}

.avatar-img {
  position: relative;
  z-index: 2;
  border: 3px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

/* Info Section */
.info-section {
  flex: 1;
  min-width: 0;
}

.name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.5rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.points-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.points-icon {
  animation: twinkle 1.5s infinite;
}

@keyframes twinkle {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

.points {
  font-size: 1.1rem;
  font-weight: 700;
  color: #FF6B6B;
}

.points-label {
  font-size: 0.9rem;
  color: #666;
}

.leaderboard-title {
  margin: 0.25rem 0 0 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: #fff;
  opacity: 0.95;
  letter-spacing: 0.5px;
}

.email-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 20px;
  font-size: 0.85rem;
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

/* Progress Section */
.progress-section {
  min-width: 100px;
}

.progress-bar {
  width: 100px;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 1s ease-out;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { opacity: 1; }
  50% { opacity: 0.8; }
  100% { opacity: 1; }
}

/* Floating Particles */
.floating-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 50%;
  opacity: 0;
  animation: floatParticle 3s infinite;
}

.particle:nth-child(1) {
  left: 20%;
  animation-delay: 0s;
}

.particle:nth-child(2) {
  left: 50%;
  animation-delay: 1s;
}

.particle:nth-child(3) {
  left: 80%;
  animation-delay: 2s;
}

@keyframes floatParticle {
  0% { 
    opacity: 0;
    transform: translateY(100px);
  }
  10% { 
    opacity: 1;
  }
  90% { 
    opacity: 1;
  }
  100% { 
    opacity: 0;
    transform: translateY(-100px);
  }
}

/* Footer */
.dialog-footer {
  padding: 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.close-footer-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease;
}

.close-footer-btn:hover {
  background: rgba(102, 126, 234, 0.1) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1.5rem;
  }
  
  .header-left {
    gap: 1rem;
  }
  
  .title-text {
    font-size: 1.5rem;
  }
  
  .subtitle-text {
    font-size: 0.9rem;
  }
  
  .content-wrapper {
    padding: 1rem;
  }
  
  .leaderboard-item {
    padding: 1rem;
    gap: 1rem;
  }
  
  .name {
    font-size: 1.1rem;
  }
  
  .tab-btn {
    padding: 1rem;
    font-size: 0.9rem;
  }
  
  .tab-btn span {
    display: none;
  }
  
  .progress-section {
    min-width: 60px;
  }
  
  .progress-bar {
    width: 60px;
  }
}

@media (max-width: 480px) {
  .leaderboard-item {
    flex-direction: column;
    text-align: center;
  }
  
  .progress-section {
    width: 100%;
  }
  
  .progress-bar {
    width: 100%;
  }
  
  .email-chip {
    margin-top: 0.5rem;
  }
}

/* Custom Scrollbar */
.tab-content::-webkit-scrollbar {
  width: 8px;
}

.tab-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.tab-content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.tab-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}
</style>

